import { apiGet, apiPost } from '../client';
import type {
  Device,
  DeviceAdmin,
  DeviceAdminFilters,
  DeviceClaimPayload,
  DeviceProvisionPayload,
  DeviceProvisioned,
  DeviceStatus,
  DeviceUnclaimed,
} from '@/types/api';

/**
 * Devices are addressed two different ways and this module is the only place that
 * should know it: reads take the integer primary key, lifecycle actions take the
 * opaque `device_uid`. Parameter names below say which one is expected.
 *
 * There is deliberately no create and no delete. Consumer endpoints may not mint a
 * device identity — provisioning is admin-only — and devices are never hard-deleted
 * because crash records reference them, and crash records are evidence.
 */

export function listDevices(): Promise<Device[]> {
  return apiGet<Device[]>('/devices');
}

export function listDeviceStatus(): Promise<DeviceStatus[]> {
  return apiGet<DeviceStatus[]>('/devices/status');
}

export function getDevice(deviceId: number): Promise<Device> {
  return apiGet<Device>(`/devices/${deviceId}`);
}

/**
 * Every failure here except the two vehicle cases collapses to one uniform
 * "Unable to claim device", deliberately — a distinguishable error would let an
 * attacker probe which device ids exist and what state they are in.
 */
export function claimDevice(payload: DeviceClaimPayload): Promise<Device> {
  return apiPost<Device>('/devices/claim', { body: payload });
}

/** Returns a rotated claim code that is shown exactly once and never retrievable. */
export function unclaimDevice(deviceUid: string): Promise<DeviceUnclaimed> {
  return apiPost<DeviceUnclaimed>(`/devices/${deviceUid}/unclaim`);
}

/** Terminal. A revoked device's telemetry is rejected outright. */
export function revokeDevice(deviceUid: string): Promise<Device> {
  return apiPost<Device>(`/devices/${deviceUid}/revoke`);
}

/** Administrator only. Terminal — the hardware came back to us. */
export function rmaDevice(deviceUid: string): Promise<Device> {
  return apiPost<Device>(`/devices/${deviceUid}/rma`);
}

/**
 * Administrator only. The whole registry, with owner and manufacturing metadata.
 * Separate from listDevices because that response is also read fleet-wide by fleet
 * managers, who have no need for every owner's name and email.
 */
export function listDevicesAdmin(filters: DeviceAdminFilters = {}): Promise<DeviceAdmin[]> {
  return apiGet<DeviceAdmin[]>('/devices/admin', {
    query: { state: filters.state, q: filters.q || undefined },
  });
}

/**
 * Administrator only. Brings a revoked device back to `unclaimed`, or releases a
 * claim lockout.
 *
 * Restoring a revoked unit rotates its claim code and returns the new one exactly
 * once — it was revoked because it was lost or compromised, so the code printed on
 * its housing cannot stay valid. Releasing a lockout returns null: that device is
 * only guarded against guessing, and its real printed code must keep working.
 */
export function restoreDevice(deviceUid: string): Promise<DeviceUnclaimed> {
  return apiPost<DeviceUnclaimed>(`/devices/${deviceUid}/restore`);
}

/** Administrator only. The response is the one place secrets exist in plaintext. */
export function provisionDevices(payload: DeviceProvisionPayload): Promise<DeviceProvisioned[]> {
  return apiPost<DeviceProvisioned[]>('/devices/provision', { body: payload });
}
